import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'
import type { Option } from 'types/entity'
import { toLegacyDateTime } from './ContactCheckboxes'

interface TaskFormProps {
  entityId: string
  onSuccess?: (msg: string) => void
  onCancel?: () => void
}

/** Port of the legacy Add Task action (InsertNewTaskOrAppointment, IsTask). */
export function TaskForm({ entityId, onSuccess }: TaskFormProps) {
  const [users, setUsers] = useState<Option[]>([])
  const [taskTypes, setTaskTypes] = useState<Option[]>([])
  const [outcomes, setOutcomes] = useState<Option[]>([])

  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [taskType, setTaskType] = useState<string>('')
  const [outcomesId, setOutcomesId] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [assignedTo, setAssignedTo] = useState<string>('')
  const [collaborators, setCollaborators] = useState<string[]>([])
  const [remarks, setRemarks] = useState<string>('')

  const [notifyBySms, setNotifyBySms] = useState<boolean>(false)
  const [notifyByEmail, setNotifyByEmail] = useState<boolean>(false)
  const [dltRegistered, setDltRegistered] = useState<boolean>(true)
  const [dltMessage, setDltMessage] = useState<string>('')

  const [loading, setLoading] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    let active = true
    async function loadData() {
      try {
        const [uList, tList, oList, dlt] = await Promise.all([
          api.fetchAssignedUsers(),
          api.fetchTaskTypes(),
          api.fetchPredefinedOutcomes(),
          api.fetchDltTemplateStatus('task reminder').catch(() => ({
            isRegistered: false,
            dltTemplateId: '',
            message: 'DLT registration status could not be checked.',
          })),
        ])
        if (!active) return
        setUsers(uList)
        setTaskTypes(tList)
        setOutcomes(oList)
        if (uList.length > 0) setAssignedTo(uList[0].id)
        setDltRegistered(dlt.isRegistered)
        setDltMessage(dlt.message)
      } catch {
        if (active) setError('Failed to load task settings.')
      } finally {
        if (active) setLoading(false)
      }
    }
    void loadData()
    return () => {
      active = false
    }
  }, [])

  /** Task-type change reloads the outcome list scoped to that type. */
  const handleTaskTypeChange = async (value: string) => {
    setTaskType(value)
    setOutcomesId('')
    try {
      setOutcomes(value ? await api.fetchOutcomesByTaskType(value) : await api.fetchPredefinedOutcomes())
    } catch {
      setOutcomes([])
    }
  }

  const handleReset = () => {
    setTitle('')
    setDescription('')
    setTaskType('')
    setOutcomesId('')
    setStartDate('')
    setEndDate('')
    if (users.length > 0) setAssignedTo(users[0].id)
    setCollaborators([])
    setRemarks('')
    setNotifyBySms(false)
    setNotifyByEmail(false)
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Task title is required.')
      return
    }
    if (!endDate) {
      setError('End date is required.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      const msg = await api.submitActionTask({
        entityId,
        title,
        description,
        taskType,
        outcomesId,
        startDate: toLegacyDateTime(startDate),
        endDate: toLegacyDateTime(endDate),
        assignedTo,
        collaboratorIds: collaborators.join(','),
        remarks,
        notifyBySms,
        notifyByEmail,
      })
      onSuccess?.(msg)
    } catch (err) {
      setError((err as Error).message || 'Failed to create task.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="widget-loader">Loading Task settings…</div>

  return (
    <div className="action-form">
      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="tk-title">
          Title <span className="req">*</span>
        </label>
        <input
          id="tk-title"
          type="text"
          className="form-control"
          placeholder="Task title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-description">
          Description
        </label>
        <textarea
          id="tk-description"
          rows={2}
          className="form-control"
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-type">
          Task Type
        </label>
        <select
          id="tk-type"
          className="form-control"
          value={taskType}
          onChange={(e) => void handleTaskTypeChange(e.target.value)}
        >
          <option value="">-- Select Task Type --</option>
          {taskTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-outcome">
          Outcome
        </label>
        <select
          id="tk-outcome"
          className="form-control"
          value={outcomesId}
          onChange={(e) => setOutcomesId(e.target.value)}
        >
          <option value="">-- Select Outcome --</option>
          {outcomes.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-start">
          Start Date &amp; Time
        </label>
        <input
          id="tk-start"
          type="datetime-local"
          className="form-control"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-end">
          End Date &amp; Time <span className="req">*</span>
        </label>
        <input
          id="tk-end"
          type="datetime-local"
          className="form-control"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-assigned">
          Assigned To
        </label>
        <select
          id="tk-assigned"
          className="form-control"
          value={assignedTo}
          onChange={(e) => setAssignedTo(e.target.value)}
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-collab">
          Collaborators
        </label>
        <select
          id="tk-collab"
          className="form-control"
          multiple
          size={Math.min(4, Math.max(2, users.length))}
          value={collaborators}
          onChange={(e) =>
            setCollaborators(Array.from(e.target.selectedOptions, (option) => option.value))
          }
        >
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tk-remarks">
          Remarks
        </label>
        <textarea
          id="tk-remarks"
          rows={2}
          className="form-control"
          placeholder="Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </div>

      <div className="form-group flex-row">
        <label
          className="checkbox-label"
          htmlFor="tk-notify-sms"
          title={!dltRegistered ? dltMessage : undefined}
        >
          <input
            id="tk-notify-sms"
            type="checkbox"
            disabled={!dltRegistered}
            checked={notifyBySms}
            onChange={(e) => setNotifyBySms(e.target.checked)}
          />
          <span>Notify by SMS</span>
        </label>
        <label className="checkbox-label" htmlFor="tk-notify-email">
          <input
            id="tk-notify-email"
            type="checkbox"
            checked={notifyByEmail}
            onChange={(e) => setNotifyByEmail(e.target.checked)}
          />
          <span>Notify by Email</span>
        </label>
      </div>
      {!dltRegistered && dltMessage && <div className="form-hint">{dltMessage}</div>}

      <div className="action-form-footer">
        <button type="button" className="btn btn-default" onClick={handleReset} disabled={submitting}>
          Reset
        </button>
        <button type="button" className="btn pri-btn" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Saving…' : 'Add Task'}
        </button>
      </div>
    </div>
  )
}
